import EditIcon from '@mui/icons-material/Edit';
import React, { useMemo, useState } from 'react';
import {
    Currency,
    DemeterChartSeriesSourceModel,
    DemeterDataFrequency,
    DemeterFilterTimeSpan,
    GetDemeterChartSeriesDataResponse,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import { chartColors, IChartDataSeries } from '../../../Components/Charts/ChartDefinitions';
import ChartWrapper from '../../../Components/Charts/ChartWrapper/ChartWrapper';
import CustomChartRaw from '../../../Components/Charts/Custom/CustomChartRaw';
import FilterTimeSpans from '../../../Components/Charts/FilterTimeSpans/FilterTimeSpans';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from '../ChartBuilder.module.scss';
import ChartBuilderSaveAction from '../ChartBuilderActions/ChartBuilderSaveAction';
import { ChartBuilderRequest, DataSourceSelection, defaultChartName } from '../ChartBuilderDefinitions';
import ChartBuilderEditTitleModal from './ChartBuilderEditTitleModal';

interface IChartBuilderChartDisplay {
    chartBuilderUpsertRequest: ChartBuilderRequest;
    setChartBuilderUpsertRequest: (chartBuilderRequest: ChartBuilderRequest) => void;
    listDemeterChartDataResponse?: GetDemeterChartSeriesDataResponse[];
    isValidTransformationTypeAndValue: (dataSource: DemeterChartSeriesSourceModel) => boolean;
}

const defaultDataFrequency = DemeterDataFrequency.Monthly;

const ChartBuilderChartDisplay: React.FC<IChartBuilderChartDisplay> = (props: IChartBuilderChartDisplay) => {
    const [translations, translate] = useLanguage();
    const [saveAsModalOpen, setSaveAsModalOpen] = useState(false);

    const titleString = useMemo(() => {
        if (!props.chartBuilderUpsertRequest.name) {
            return defaultChartName;
        }

        return props.chartBuilderUpsertRequest.name;
    }, [props.chartBuilderUpsertRequest.name]);

    const dataSourceTag = useMemo(() => {
        const sourceTags = [];

        const tags = Array.from(
            new Set(
                props.listDemeterChartDataResponse
                    ?.map((x) => x.seriesData?.sourceData.map((y) => y.dataSourceTags))
                    .flat()
                    .filter(Boolean),
            ),
        );

        sourceTags.push(...tags, translations.dataSource.StoneXCalculations);

        return sourceTags as string[];
    }, [props.listDemeterChartDataResponse]);

    const linesSeries = useMemo<IChartDataSeries[]>(() => {
        if (!props.listDemeterChartDataResponse?.length || props.listDemeterChartDataResponse.length === 0) {
            return [];
        }

        const dataSourceRows = props.listDemeterChartDataResponse
            .map((x) =>
                x.seriesData?.sourceData.map((y) => {
                    const matchingSourceFromRequest = props.chartBuilderUpsertRequest.rows
                        .map((z) => z.sources)
                        .flat()
                        .find(
                            (z) =>
                                z.commodity === y.commodity &&
                                z.region === y.region &&
                                z.subRegion === y.subRegion &&
                                z.dataFrequency === y.dataFrequency &&
                                z.leadingIndicatorType === y.leadingIndicatorType &&
                                z.extraParameters === y.extraParameters,
                        ) as DataSourceSelection;

                    const isValid = (string?: string | null) => string && string !== null;

                    const commodityString = isValid(matchingSourceFromRequest?.commodityDisplayName)
                        ? translate(matchingSourceFromRequest?.commodityDisplayName ?? '')
                        : translations.commodity[matchingSourceFromRequest?.commodity!];

                    const subCommodityString = isValid(matchingSourceFromRequest?.subCommodityDisplayName)
                        ? translate(matchingSourceFromRequest?.subCommodityDisplayName ?? '')
                        : translate(matchingSourceFromRequest?.subCommodity ?? '');

                    return {
                        ...y,
                        name: matchingSourceFromRequest?.name || subCommodityString || commodityString,
                        rows: y.rows,
                        includeInChart: matchingSourceFromRequest?.includeInChart,
                        axis: matchingSourceFromRequest?.axis,
                        color: matchingSourceFromRequest?.color,
                        style: matchingSourceFromRequest?.style,
                    };
                }),
            )
            .flat();
        const lengthOfDataSourceRows = dataSourceRows.length;

        const dataSeriesLines = props.listDemeterChartDataResponse.map((x, index) => {
            const matchingSeriesFromRequest = props.chartBuilderUpsertRequest.rows[index];

            return {
                ...x.seriesData,
                name: matchingSeriesFromRequest?.name || `${translations.chartBuilder.headers.calculatedSeries} ${index + 1}`,
                rows: x.seriesData?.rows,
                includeInChart: true,
                axis: matchingSeriesFromRequest?.axis,
                color: matchingSeriesFromRequest?.color ?? chartColors.lineChartColorsRuleSetOne[lengthOfDataSourceRows + index + 1],
                style: matchingSeriesFromRequest?.style,
            };
        });

        const allLines = [...dataSourceRows, dataSeriesLines]
            .flat()
            .filter(Boolean)
            .filter((y) => y?.includeInChart)
            .filter((y) => y!.rows!.length > 0);

        return allLines.map((x, index) => {
            // TODO: Can we get UI for using custom names? Should we change this?
            const label = x!.name || `Series ${index + 1}`;
            const noIsActualValueData = x!.rows?.every((y) => !y.isActualValue);
            const lastIndexOfIsActualData = x!.rows?.findLastIndex((y) => y.isActualValue) ?? 0;
            const line: IChartDataSeries = {
                label,
                forecastLabel: `${label} ${translations.words.forecast}`,
                data: x!.rows!.map((y, dataItemIndex) => {
                    let isActualValue = noIsActualValueData ? new Date(y?.asOfDate!).getTime() < new Date().getTime() : y.isActualValue;
                    if (dataItemIndex <= lastIndexOfIsActualData) {
                        isActualValue = true;
                    }

                    return {
                        value: y?.value,
                        asOfDate: new Date(y.asOfDate),
                        isActualValue,
                    };
                }),
                yAxis: x!.axis!,
                style: x!.style,
            };

            if (x!.color) {
                line.color = x!.color ?? '';
            }

            return line;
        }) as IChartDataSeries[];
    }, [props.listDemeterChartDataResponse]);

    const footer = useMemo(() => {
        if (!props.chartBuilderUpsertRequest?.timespanOptions?.length || !props.chartBuilderUpsertRequest.showTimespanOptions) {
            return null;
        }

        const currentDataSeries = [...props.chartBuilderUpsertRequest.rows];

        return (
            <FilterTimeSpans
                name="CustomChart"
                filterTimeSpanOptions={props.chartBuilderUpsertRequest?.timespanOptions as DemeterFilterTimeSpan[]}
                filterTimeSpan={props.chartBuilderUpsertRequest.rows[0].filterTimeSpan}
                handleTimeSpanSelected={(filterTimeSpan) =>
                    props.setChartBuilderUpsertRequest({
                        ...props.chartBuilderUpsertRequest,
                        rows: currentDataSeries?.map((x) => ({
                            ...x,
                            filterTimeSpan,
                            sources: x.sources.map((y) => ({ ...y, filterTimeSpan })),
                        })),
                    })
                }
                track={{ eventCategory: 'ChartBuilder', eventAction: 'TimeSpanChanged', eventValue: props.chartBuilderUpsertRequest.rows[0].filterTimeSpan }}
            />
        );
    }, [props.listDemeterChartDataResponse, props.chartBuilderUpsertRequest?.timespanOptions]);

    // TODO: use debounce and determine when to load.
    const loading = false;

    // TODO: Can I get the axis on the list response?
    const unitOfMeasures = useMemo(() => {
        const currentUnitOfMeasure = {} as { y1: UnitOfMeasure; y2: UnitOfMeasure };
        if (!props.chartBuilderUpsertRequest || !props.listDemeterChartDataResponse) {
            return currentUnitOfMeasure;
        }

        props.chartBuilderUpsertRequest.rows.forEach((x, seriesIndex) => {
            x.sources.forEach((y, sourceIndex) => {
                const matchingSourceFromResponse = props.listDemeterChartDataResponse![seriesIndex]?.seriesData?.sourceData[sourceIndex]!;
                const unitOfMeasureAvailable = matchingSourceFromResponse?.unitOfMeasure ?? y.unitOfMeasure;

                if (!unitOfMeasureAvailable || (currentUnitOfMeasure.y1 && currentUnitOfMeasure.y2)) {
                    return;
                }

                if (!!unitOfMeasureAvailable && y.axis === 0 && !currentUnitOfMeasure.y1) {
                    currentUnitOfMeasure.y1 = unitOfMeasureAvailable;
                    return;
                }

                if (!!unitOfMeasureAvailable && y.axis === 1 && !currentUnitOfMeasure.y2) {
                    currentUnitOfMeasure.y2 = unitOfMeasureAvailable;
                }
            });
        });

        return currentUnitOfMeasure;
    }, [linesSeries, props.chartBuilderUpsertRequest, props.listDemeterChartDataResponse]);

    const currencies = useMemo(() => {
        const currentCurrencies = {} as { y1: Currency; y2: Currency };
        if (!props.chartBuilderUpsertRequest) {
            return currentCurrencies;
        }

        props.chartBuilderUpsertRequest.rows.forEach((x, seriesIndex) => {
            x.sources.forEach((y, sourceIndex) => {
                const matchingSourceFromResponse = props.listDemeterChartDataResponse![seriesIndex]?.seriesData?.sourceData[sourceIndex]!;
                const currenciesAvailable = matchingSourceFromResponse?.currency ?? y.currency;

                if (!currenciesAvailable || (currentCurrencies.y1 && currentCurrencies.y2)) {
                    return;
                }

                if (!!currenciesAvailable && y.axis === 0 && !currentCurrencies.y1) {
                    currentCurrencies.y1 = currenciesAvailable;
                    return;
                }

                if (!!currenciesAvailable && y.axis === 1 && !currentCurrencies.y2) {
                    currentCurrencies.y2 = currenciesAvailable;
                }
            });
        });

        return currentCurrencies;
    }, [linesSeries, props.chartBuilderUpsertRequest]);

    return (
        <>
            {linesSeries.length > 0 && (
                <ChartBuilderSaveAction
                    chartBuilderUpsertRequest={props.chartBuilderUpsertRequest}
                    setChartBuilderUpsertRequest={props.setChartBuilderUpsertRequest}
                    isValidTransformationTypeAndValue={props.isValidTransformationTypeAndValue}
                />
            )}

            <div className={linesSeries.length === 0 ? styles.chart_display_no_series : styles.chart_display_chart}>
                <ChartWrapper
                    name="CustomChart"
                    title={titleString}
                    dataSourceTag={dataSourceTag}
                    customTitle={
                        <>
                            {titleString}
                            <div className={styles.chart_display_edit_icon}>
                                <EditIcon onClick={() => setSaveAsModalOpen(true)} />
                            </div>
                        </>
                    }
                    footer={footer}
                    isLoading={loading}
                    track={{ eventCategory: 'ChartBuilder', eventAction: 'CustomChart' }}
                >
                    <CustomChartRaw
                        linesSeries={linesSeries}
                        chartBuilderRequest={props.chartBuilderUpsertRequest}
                        dataFrequency={props.chartBuilderUpsertRequest?.rows[0].dataFrequency ?? defaultDataFrequency}
                        unitOfMeasures={unitOfMeasures}
                        currencies={currencies}
                    />
                </ChartWrapper>
            </div>
            <ChartBuilderEditTitleModal
                chartBuilderUpsertRequest={props.chartBuilderUpsertRequest}
                setChartBuilderUpsertRequest={props.setChartBuilderUpsertRequest}
                saveAsModalOpen={saveAsModalOpen}
                actionButtonName={translations.words.update}
                setSaveAsModalOpen={setSaveAsModalOpen}
                handleAction={() => setSaveAsModalOpen(false)}
            />
        </>
    );
};

export default ChartBuilderChartDisplay;
